import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { usePageMeta } from "../lib/usePageMeta";
import { CaretDown, MagnifyingGlass } from "@phosphor-icons/react";

/**
 * Category-tabbed FAQ.
 *
 * Copy discipline
 * ---------------
 *   * Every answer is grounded in current policy / shipping / documentation
 *     behavior.  No therapy / dosing / reconstitution / weight-loss /
 *     wellness / human-use claims.
 *   * Documentation policy: lot-specific documentation is reviewed and
 *     published in the COA Library when available; documents not yet
 *     published are provided on request.  We never claim that every
 *     batch automatically carries a signed / public document.
 *   * Shipping policy: lyophilized peptide reference standards ship at
 *     room temperature in tamper-evident cartons; signature-required
 *     delivery + real-time tracking on every parcel.
 *   * 106 active SKUs organized across nine chemistry categories.
 */

export const FAQ_CATEGORIES = [
  {
    id: "products",
    label: "Products & Documentation",
    testid: "faq-tab-products",
    items: [
      {
        q: "What are research reference standards?",
        a: "Research reference standards are laboratory-grade materials sold to qualified researchers for in-vitro reference and comparison work. Every listing on Medical Peptide Store is a reference standard — not a therapeutic product, and not intended for human or veterinary use.",
      },
      {
        q: "Who is Medical Peptide Store for?",
        a: "Our catalog serves qualified laboratories, universities, and research organizations sourcing reference standards for in-vitro research. Every purchase acknowledges research-use terms and signature-required delivery.",
      },
      {
        q: "What quality information is available?",
        a: "Each product page shows format, strength, storage conditions, and any lot-specific documentation available at the time of listing. Publicly visible fields are limited to what we can support from our records.",
      },
      {
        q: "Are lot-specific documents available?",
        a: "Yes. Lot-specific documentation is reviewed and published in the COA Library when it is ready. If a lot document is not yet published, you can request the record from our team and we will share what is available for that lot.",
      },
      {
        q: "Why might a document or metric not be published?",
        a: "We only publish what has been reviewed. If a metric is still pending internal review, or if a lot has not been through the full publication workflow, the field is left blank rather than filled with a placeholder value.",
      },
      {
        q: "How should unopened reference standards be stored?",
        a: "Storage conditions for each unopened lyophilized reference standard are displayed on the product page and packing list. Once opened or reconstituted, storage/handling becomes the receiving laboratory's responsibility per its own internal SOPs.",
      },
    ],
  },
  {
    id: "ordering",
    label: "Ordering & Compliance",
    testid: "faq-tab-ordering",
    items: [
      {
        q: "Can I order through the online catalog?",
        a: "Yes. Any listing shown as in stock can be purchased through secure online checkout. Availability, shipping eligibility, and order acceptance remain subject to applicable product and destination restrictions.",
      },
      {
        q: "Do I need an account?",
        a: "No. The catalog supports secure guest checkout. Creating an optional account lets you review previous orders and lot references in one place.",
      },
      {
        q: "What payment methods are accepted?",
        a: "Payment is completed through Stripe on the secure Stripe-hosted checkout page. Supported card networks and digital wallets are displayed on Stripe's checkout form at the time of purchase.",
      },
      {
        q: "Why might an order or destination be restricted?",
        a: "Some products and destinations are subject to legal, carrier, or internal-policy restrictions. If a restriction applies to your cart or shipping address, we will decline the order and refund any authorization in full.",
      },
      {
        q: "What do I acknowledge before checkout?",
        a: "You acknowledge our Research-Use Policy, Terms of Service, and Final-Sale & Claims policy. These state that products are strictly for laboratory research, not for human or veterinary use, and that sales are final except as required by law.",
      },
      {
        q: "Can an order be declined or cancelled?",
        a: "Yes. Orders can be declined or cancelled by us if there is a compliance, destination, product-availability, or documentation issue. Any charge or authorization is refunded promptly when we cancel.",
      },
    ],
  },
  {
    id: "shipping",
    label: "Shipping & Delivery",
    testid: "faq-tab-shipping",
    items: [
      {
        q: "How are lyophilized standards shipped?",
        a: "Lyophilized peptide reference standards ship at room temperature as freeze-dried powder in tamper-evident cartons. This is the standard operating packaging for the listings in this catalog.",
      },
      {
        q: "How are shipping rates calculated?",
        a: "Shipping is calculated live at checkout based on your destination and the carrier service you select. We do not use flat-fee shipping or hidden markups — you see the real quoted rate before you pay.",
      },
      {
        q: "Is a signature required?",
        a: "Yes. Every order ships with a signature-required carrier service. Someone at the receiving address must sign for the parcel; the carrier will follow its normal reattempt / hold policy if no one is present.",
      },
      {
        q: "When will I receive tracking?",
        a: "Tracking is emailed as soon as the shipping label is generated. Every parcel is tracked from label creation through carrier receipt, transit, out-for-delivery, and signature at the door.",
      },
      {
        q: "What should I do if tracking stalls or says delivered?",
        a: "First, confirm with the address recipient and check with any front-desk or receiving team. If the tracking event does not match what you see, email info@medicalpeptidestore.com within the window in our Final-Sale & Claims policy so we can open a carrier trace.",
      },
      {
        q: "What happens if an item is damaged or missing?",
        a: "Contact us within the window in our Final-Sale & Claims policy with photos of the tamper-evident carton and the parcel. We will work with the carrier and, where the policy applies, arrange a replacement or refund.",
      },
    ],
  },
  {
    id: "storage",
    label: "Storage & Handling",
    testid: "faq-tab-storage",
    items: [
      {
        q: "Where can I find storage information?",
        a: "Storage conditions for each unopened reference standard are shown on the product page and repeated on the packing list included with the parcel.",
      },
      {
        q: "What does lyophilized mean?",
        a: "Lyophilized (freeze-dried) means the material has been dehydrated under vacuum from the frozen state into a stable powder form. Lyophilized reference standards ship at room temperature in tamper-evident cartons.",
      },
      {
        q: "Does Medical Peptide Store provide dosing or reconstitution guidance?",
        a: "No. Products are research reference standards sold strictly for laboratory research only. We do not provide dosing, reconstitution, protocol, or any human or veterinary use guidance. Each receiving laboratory follows its own internal SOPs.",
      },
    ],
  },
  {
    id: "support",
    label: "Account & Support",
    testid: "faq-tab-support",
    items: [
      {
        q: "Where can I find my order confirmation or tracking?",
        a: "Stripe emails the receipt to the address you provide at checkout, and a shipment-tracking email follows as soon as the carrier label is generated. If you also created an account, previous orders appear under your account dashboard.",
      },
      {
        q: "How do I request documentation for a lot?",
        a: "Email info@medicalpeptidestore.com with the product name and the lot reference from your packing list. We will confirm what has been reviewed and share what is available for that lot.",
      },
      {
        q: "How do I contact support or ask about bulk orders?",
        a: "Email info@medicalpeptidestore.com or call (888) 294-7717 during our displayed support hours. For bulk inquiries, describe the compounds and quantities you are evaluating and we will respond with what the catalog can support.",
      },
    ],
  },
];

const ALL_ITEMS = FAQ_CATEGORIES.flatMap((c) => c.items.map((it) => ({ ...it, catId: c.id, catLabel: c.label })));

export default function FAQ() {
  const [activeId, setActiveId] = useState(FAQ_CATEGORIES[0].id);
  const [search, setSearch] = useState("");
  const [openIdx, setOpenIdx] = useState(-1);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (q) {
      return ALL_ITEMS.filter(
        ({ q: question, a }) =>
          question.toLowerCase().includes(q) || a.toLowerCase().includes(q),
      );
    }
    return FAQ_CATEGORIES.find((c) => c.id === activeId)?.items || [];
  }, [activeId, search]);

  usePageMeta({
    title: "FAQ · Support",
    description:
      "Answers to common questions about ordering, lyophilized shipping, signature-required delivery, storage, lot documentation and support at Medical Peptide Store.",
    canonicalPath: "/faq",
    jsonLd: [{
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": ALL_ITEMS.map(({ q, a }) => ({
        "@type": "Question",
        "name": q,
        "acceptedAnswer": { "@type": "Answer", "text": a },
      })),
    }],
  });

  const totalQs = ALL_ITEMS.length;

  return (
    <div className="bg-brand-surface min-h-screen" data-testid="faq-page">
      {/* Header block */}
      <section className="bg-brand-navy text-white">
        <div className="mps-container py-16 md:py-20">
          <div className="max-w-3xl">
            <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-brand-accent mb-4">
              Support · {totalQs} answers
            </div>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] !text-white">
              Straight answers about ordering, shipping,
              <br className="hidden md:block" />
              storage, and documentation.
            </h1>
            <p className="mt-6 text-lg text-white/80 leading-relaxed max-w-2xl font-light">
              Grounded in our current policies and operations. No therapy
              claims, no dosing guidance, no marketing gloss.
            </p>

            {/* Search — filters across all categories */}
            <div className="mt-10 relative max-w-xl">
              <MagnifyingGlass
                size={16}
                weight="regular"
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white/60"
                aria-hidden
              />
              <input
                type="search"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setOpenIdx(-1); }}
                placeholder="Search FAQ…"
                data-testid="faq-search-input"
                className="w-full bg-white/10 border border-white/20 rounded-full pl-11 pr-4 py-3 text-sm text-white placeholder:text-white/50 focus:bg-white/15 focus:border-brand-accent focus:outline-none transition-colors"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Category tabs */}
      {!search.trim() && (
        <div className="border-b border-brand-border bg-white sticky top-20 z-20" data-testid="faq-tabs">
          <div className="mps-container">
            <div className="flex gap-2 overflow-x-auto py-4 -mx-2 px-2 scrollbar-thin">
              {FAQ_CATEGORIES.map((cat) => {
                const active = cat.id === activeId;
                return (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => { setActiveId(cat.id); setOpenIdx(-1); }}
                    data-testid={cat.testid}
                    className={
                      "shrink-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap transition-colors " +
                      (active
                        ? "bg-brand-navy text-white"
                        : "bg-brand-surface text-brand-navy hover:bg-brand-surface2 border border-brand-border")
                    }
                    aria-pressed={active}
                  >
                    {cat.label}
                    <span className={active ? "text-white/70" : "text-brand-muted"}>
                      {cat.items.length}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Q&A list */}
      <div className="mps-container py-14 md:py-16">
        {visible.length === 0 && (
          <div className="mps-card p-10 text-center" data-testid="faq-empty">
            <div className="font-display text-xl font-semibold text-brand-navy">
              No matching answers.
            </div>
            <p className="mt-2 text-brand-muted text-sm">
              Try a broader search term, or email
              {" "}<a className="text-brand-accent hover:underline" href="mailto:info@medicalpeptidestore.com">info@medicalpeptidestore.com</a>{" "}
              and we will respond.
            </p>
          </div>
        )}
        <div className="max-w-3xl mx-auto divide-y divide-brand-border rounded-[14px] bg-white border border-brand-border overflow-hidden">
          {visible.map((item, i) => {
            const open = openIdx === i;
            return (
              <div key={`${item.catId || "s"}-${i}`} data-testid={`faq-item-${i}`}>
                <button
                  type="button"
                  onClick={() => setOpenIdx(open ? -1 : i)}
                  aria-expanded={open}
                  aria-controls={`faq-answer-${i}`}
                  data-testid={`faq-q-${i}`}
                  className="w-full text-left px-6 py-5 flex items-center justify-between gap-4 hover:bg-brand-surface transition-colors"
                >
                  <div>
                    {search.trim() && (
                      <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-brand-accent mb-1">
                        {item.catLabel}
                      </div>
                    )}
                    <span className="font-display text-brand-navy text-base font-medium leading-snug">
                      {item.q}
                    </span>
                  </div>
                  <CaretDown
                    size={18}
                    weight="regular"
                    className={
                      "shrink-0 text-brand-navy transition-transform duration-200 " +
                      (open ? "rotate-180" : "")
                    }
                    aria-hidden
                  />
                </button>
                {open && (
                  <div
                    id={`faq-answer-${i}`}
                    className="px-6 pb-6 -mt-1 text-brand-muted text-[15px] leading-relaxed"
                    data-testid={`faq-a-${i}`}
                  >
                    {item.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Support call-to-action */}
        <div className="max-w-3xl mx-auto mt-10 mps-card p-7 flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6">
          <div className="flex-1">
            <div className="font-display text-lg font-semibold text-brand-navy">
              Still have a question?
            </div>
            <p className="mt-1 text-sm text-brand-muted">
              A person answers every message. Email us and we will respond
              during our support hours.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <a
              href="mailto:info@medicalpeptidestore.com"
              data-testid="faq-support-email"
              className="inline-flex items-center gap-2 h-11 px-5 rounded-[12px] bg-brand-navy hover:bg-brand-navy2 text-white font-semibold text-sm transition-colors"
            >
              info@medicalpeptidestore.com
            </a>
            <Link
              to="/contact"
              data-testid="faq-support-contact"
              className="inline-flex items-center gap-2 h-11 px-5 rounded-[12px] border border-brand-border text-brand-navy hover:bg-brand-surface font-semibold text-sm transition-colors"
            >
              Contact page
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
